import { useState, useEffect } from 'react'
import {
  Card, Table, Button, Input, Select, Space, Tag, Modal, Form,
  InputNumber, message, Popconfirm, Row, Col, Typography
} from 'antd'
import { PlusOutlined, SearchOutlined, EditOutlined, DeleteOutlined, ReloadOutlined } from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import api from '../services/api'

const { Option } = Select
const { TextArea } = Input

interface EnterpriseClient {
  id: number
  user_id: number
  username: string
  phone: string
  user_status: number
  company_name: string
  company_code: string
  contact_name: string
  contact_phone: string
  delivery_address: string
  credit_limit: number
  settlement_type: number
  monthly_status: number
  status: number
  created_at: string
}

const settlementMap: Record<number, string> = { 1: '即时结算', 2: '月结' }
const settlementColor: Record<number, string> = { 1: 'blue', 2: 'purple' }
const monthlyStatusMap: Record<number, { label: string; color: string }> = {
  0: { label: '未申请', color: 'default' },
  1: { label: '待审核', color: 'orange' },
  2: { label: '已开通', color: 'green' },
  3: { label: '已拒绝', color: 'red' }
}

export default function EnterpriseClients() {
  const [data, setData] = useState<EnterpriseClient[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [keyword, setKeyword] = useState('')
  const [status, setStatus] = useState<number | undefined>(undefined)

  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<EnterpriseClient | null>(null)
  const [form] = Form.useForm()
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => { load() }, [page, pageSize, keyword, status])

  const load = async () => {
    setLoading(true)
    try {
      const res: any = await api.get('/enterprise-clients', { params: { page, page_size: pageSize, keyword, status } })
      setData(res.data?.list || [])
      setTotal(res.data?.total || 0)
    } catch {
      message.error('加载失败')
    } finally {
      setLoading(false)
    }
  }

  const openCreate = () => { setEditing(null); form.resetFields(); setModalOpen(true) }

  const openEdit = (record: EnterpriseClient) => {
    setEditing(record)
    form.setFieldsValue({
      username: record.username,
      phone: record.phone,
      company_name: record.company_name,
      company_code: record.company_code,
      contact_name: record.contact_name,
      contact_phone: record.contact_phone,
      delivery_address: record.delivery_address,
      credit_limit: record.credit_limit,
      settlement_type: record.settlement_type,
      status: record.status
    })
    setModalOpen(true)
  }

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields()
      setSubmitting(true)
      if (editing) {
        await api.put(`/enterprise-clients/${editing.id}`, values)
        message.success('更新成功')
      } else {
        await api.post('/enterprise-clients', values)
        message.success('创建成功')
      }
      setModalOpen(false)
      load()
    } catch (err: any) {
      if (!err.message?.includes('validate')) {
        message.error(err?.response?.data?.message || '操作失败')
      }
    } finally {
      setSubmitting(false)
    }
  }

  const handleDelete = async (id: number) => {
    try {
      await api.delete(`/enterprise-clients/${id}`)
      message.success('删除成功')
      load()
    } catch (err: any) {
      message.error(err?.response?.data?.message || '删除失败')
    }
  }

  const handleToggleStatus = async (record: EnterpriseClient) => {
    const newStatus = record.status === 1 ? 0 : 1
    try {
      await api.put(`/enterprise-clients/${record.id}`, { status: newStatus })
      message.success(newStatus === 1 ? '已启用' : '已禁用')
      load()
    } catch (err: any) {
      message.error(err?.response?.data?.message || '操作失败')
    }
  }

  const columns: ColumnsType<EnterpriseClient> = [
    {
      title: '企业名称',
      dataIndex: 'company_name',
      key: 'company_name',
      width: 180,
      render: v => <span style={{ fontWeight: 600 }}>{v || '-'}</span>
    },
    {
      title: '登录账号',
      key: 'account',
      width: 150,
      render: (_, r) => (
        <div>
          <div>{r.username || '-'}</div>
          <div style={{ fontSize: 12, color: '#888' }}>{r.phone || '-'}</div>
        </div>
      )
    },
    {
      title: '联系人',
      key: 'contact',
      width: 130,
      render: (_, r) => (
        <div>
          <div>{r.contact_name || '-'}</div>
          <div style={{ fontSize: 12, color: '#888' }}>{r.contact_phone || '-'}</div>
        </div>
      )
    },
    {
      title: '信用额度',
      dataIndex: 'credit_limit',
      key: 'credit_limit',
      width: 100,
      render: v => v > 0 ? <span style={{ color: '#52c41a', fontWeight: 600 }}>¥{parseFloat(String(v)).toLocaleString()}</span> : '—'
    },
    {
      title: '结算方式',
      dataIndex: 'settlement_type',
      key: 'settlement_type',
      width: 90,
      render: v => <Tag color={settlementColor[v] || 'default'}>{settlementMap[v] || '未知'}</Tag>
    },
    {
      title: '月结状态',
      dataIndex: 'monthly_status',
      key: 'monthly_status',
      width: 90,
      render: v => {
        const s = monthlyStatusMap[v] || monthlyStatusMap[0]
        return <Tag color={s.color}>{s.label}</Tag>
      }
    },
    {
      title: '账号状态',
      dataIndex: 'status',
      key: 'status',
      width: 90,
      render: v => v === 1
        ? <Tag color="green">正常</Tag>
        : <Tag color="red">禁用</Tag>
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 100,
      render: v => v ? String(v).slice(0, 10) : '-'
    },
    {
      title: '操作',
      key: 'action',
      width: 160,
      fixed: 'right',
      render: (_, r) => (
        <Space size="small">
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} />
          <Button
            size="small"
            type={r.status === 1 ? 'default' : 'primary'}
            danger={r.status === 1}
            onClick={() => handleToggleStatus(r)}
          >
            {r.status === 1 ? '禁用' : '启用'}
          </Button>
          <Popconfirm
            title="确认删除该企业客户？"
            description="删除后该账号将无法登录，所有关联数据将被清除"
            onConfirm={() => handleDelete(r.id)}
            okText="确认删除"
            okButtonProps={{ danger: true }}
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      )
    }
  ]

  return (
    <div>
      <Card
        title={<Typography.Title level={4} style={{ margin: 0 }}>企业客户管理</Typography.Title>}
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}
            style={{ background: '#55266c', borderColor: '#55266c' }}>
            新增企业客户
          </Button>
        }
        styles={{ body: { padding: 0 } }}
      >
        {/* 搜索栏 */}
        <div style={{ padding: '16px 16px 0' }}>
          <Space wrap>
            <Input
              placeholder="搜索企业名称 / 账号 / 手机号"
              prefix={<SearchOutlined />}
              value={keyword}
              onChange={e => { setKeyword(e.target.value); setPage(1) }}
              style={{ width: 260 }}
              allowClear
            />
            <Select
              placeholder="账号状态"
              allowClear
              value={status}
              onChange={v => { setStatus(v); setPage(1) }}
              style={{ width: 120 }}
            >
              <Option value={1}>正常</Option>
              <Option value={0}>禁用</Option>
            </Select>
            <Button icon={<ReloadOutlined />} onClick={load}>刷新</Button>
          </Space>
        </div>

        <Table
          columns={columns}
          dataSource={data}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1000 }}
          style={{ marginTop: 8 }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: t => `共 ${t} 条`,
            onChange: (p, ps) => { setPage(p); setPageSize(ps) }
          }}
        />
      </Card>

      {/* 新增/编辑弹窗 */}
      <Modal
        title={editing ? '编辑企业客户' : '新增企业客户'}
        open={modalOpen}
        onOk={handleSubmit}
        onCancel={() => setModalOpen(false)}
        confirmLoading={submitting}
        okText={editing ? '保存' : '创建'}
        width={600}
        destroyOnClose
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          {!editing && (
            <>
              <Typography.Title level={5} style={{ color: '#55266c' }}>账号信息</Typography.Title>
              <Row gutter={12}>
                <Col span={12}>
                  <Form.Item name="username" label="登录用户名" rules={[{ required: true, message: '请输入用户名' }]}>
                    <Input placeholder="由平台提供，首次登录时设置密码" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="phone" label="手机号" rules={[{ required: true, message: '请输入手机号' }]}>
                    <Input placeholder="企业联系人手机号" />
                  </Form.Item>
                </Col>
              </Row>
              <Typography.Title level={5} style={{ color: '#55266c', marginTop: 8 }}>企业信息</Typography.Title>
            </>
          )}
          {editing && (
            <Row gutter={12}>
              <Col span={12}>
                <Form.Item name="company_name" label="企业名称" rules={[{ required: true }]}>
                  <Input />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="company_code" label="企业编码">
                  <Input />
                </Form.Item>
              </Col>
            </Row>
          )}
          {!editing && (
            <Row gutter={12}>
              <Col span={12}>
                <Form.Item name="company_name" label="企业名称" rules={[{ required: true, message: '请输入企业名称' }]}>
                  <Input placeholder="请输入企业全称" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="company_code" label="企业编码">
                  <Input placeholder="选填" />
                </Form.Item>
              </Col>
            </Row>
          )}
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="contact_name" label="联系人" rules={[{ required: true, message: '请输入联系人' }]}>
                <Input placeholder="企业对接人姓名" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="contact_phone" label="联系电话" rules={[{ required: true, message: '请输入联系电话' }]}>
                <Input placeholder="联系人手机号" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="delivery_address" label="默认配送地址">
            <TextArea rows={2} placeholder="企业收货地址（选填）" />
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="credit_limit" label="信用额度（元）" rules={[{ required: true }]}>
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  step={1000}
                  precision={2}
                  placeholder="月结额度上限"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="settlement_type" label="结算方式" rules={[{ required: true }]}>
                <Select placeholder="选择结算方式">
                  <Option value={1}>即时结算（预付款）</Option>
                  <Option value={2}>月结（信用额度）</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          {editing && (
            <Form.Item name="status" label="账号状态">
              <Select>
                <Option value={1}>正常</Option>
                <Option value={0}>禁用</Option>
              </Select>
            </Form.Item>
          )}
          {!editing && (
            <div style={{ background: '#f0f7ff', padding: 12, borderRadius: 8, fontSize: 13, color: '#555' }}>
              <strong>创建说明：</strong>账号创建后，企业员工使用<strong>用户名</strong>首次登录平台，
              首次登录时需设置密码。企业客户默认状态为<strong>正常</strong>。
            </div>
          )}
        </Form>
      </Modal>
    </div>
  )
}
